import type {
  PlatformStatus,
  ModelHttpRequestDetail,
  ModelHttpRequestListResponse,
  ModelHttpRequestSummary,
  TraceDetail,
  TraceEvent,
  TraceFilters,
  TraceListResponse,
  TraceSource,
  TraceStatus,
  ViewerIdentity,
} from '@maland/contracts';

export type {
  AiRoundtripDetail,
  PlatformStatus,
  ModelHttpRequestDetail,
  ModelHttpRequestListResponse,
  ModelHttpRequestSummary,
  ServiceState,
  TraceDetail,
  TraceEvent,
  TraceFilters,
  TraceListItem,
  TraceListResponse,
  TraceSource,
  TraceStatus,
  ViewerIdentity,
} from '@maland/contracts';

/** Mutable form state; converted to the readonly shared TraceFilters DTO at the API boundary. */
export interface TraceFilterForm {
  roomId: string;
  runId: string;
  playerId: string;
  source: TraceSource | '';
  status: TraceStatus | '';
}

/** Presentation-only fields derived from the versioned TraceEvent contract. */
export interface TimelineViewItem {
  event: TraceEvent;
  offsetMs: number;
  title: string;
  summary?: string;
  status: TraceStatus;
}

export interface TraceDetailView extends Omit<TraceDetail, 'timeline'> {
  timeline: TimelineViewItem[];
}

export interface DashboardData {
  viewer: ViewerIdentity;
  status: PlatformStatus;
  traces: TraceListResponse;
}

/** GET /api/audit response projection; the API event shape predates the shared AuditRecord DTO. */
export interface AuditEventDto {
  type: 'gm_api.audit';
  occurredAt: string;
  requestId: string;
  traceId: string;
  actor: {
    subject: string;
    email: string;
    role: 'viewer' | 'operator' | 'admin';
  } | null;
  action: string;
  resource: string;
  outcome: 'succeeded' | 'rejected' | 'failed';
  status: number;
  durationMs: number;
}

export interface AuditListResponse {
  items: AuditEventDto[];
  nextCursor?: string;
}

export const toTraceFilters = (form: TraceFilterForm): TraceFilters => ({
  ...(form.roomId ? { roomId: form.roomId } : {}),
  ...(form.runId ? { runId: form.runId } : {}),
  ...(form.playerId ? { playerId: form.playerId } : {}),
  ...(form.source ? { source: form.source } : {}),
  ...(form.status ? { status: form.status } : {}),
});

const eventTitles: Partial<Record<TraceEvent['type'], string>> = {
  'run.started': '创建叙事运行',
  'run.completed': '运行结束',
  'run.failed': '运行失败',
  'ai.roundtrip.started': '发送主模型请求',
  'ai.roundtrip.completed': '模型响应完成',
  'ai.roundtrip.failed': '模型请求失败',
  'ai.text.delta': '收到旁白文本',
  'ai.tool.call': '发起表演工具调用',
  'ai.tool.result': '表演工具返回',
  'narration.queued': '旁白进入播放队列',
  'narration.started': '旁白开始播放',
  'narration.completed': '旁白播放完成',
  'narration.segment.released': '旁白段水位放行',
  'timeline.drained': '本轮播放时间线排空',
  'performance.queued': '表演动作进入队列',
  'performance.started': '客户端开始表演动作',
  'performance.completed': '客户端表演动作完成',
  'game.mode.changed': '游戏模式变更',
  'options.shown': '选项面板出现',
  'options.hidden': '选项面板隐藏',
  'player.input.submitted': '玩家提交自由输入',
  'player.action.confirmed': '玩家确认行动',
  'player.action.aborted': '玩家取消行动',
  'system.notice': '系统提示',
};

const eventStatus = (event: TraceEvent): TraceStatus => {
  if (event.type.endsWith('.failed') || event.payload.severity === 'error') return 'error';
  if (event.payload.status === 'running') return 'running';
  return 'success';
};

const eventSummary = (event: TraceEvent): string | undefined => {
  for (const key of ['summary', 'message', 'text', 'action'] as const) {
    const value = event.payload[key];
    if (typeof value === 'string') return value;
  }
  return undefined;
};

export const toTraceDetailView = (detail: TraceDetail): TraceDetailView => {
  const startedAt = Date.parse(detail.startedAt);
  return {
    ...detail,
    timeline: detail.timeline.map((event) => {
      const summary = eventSummary(event);
      return {
        event,
        offsetMs: Math.max(0, Date.parse(event.occurredAt) - startedAt),
        title: eventTitles[event.type] ?? event.type,
        ...(summary ? { summary } : {}),
        status: eventStatus(event),
      };
    }),
  };
};
